import { Box, Text } from "@primer/react";
import { AlertIcon, ClockIcon, CreditCardIcon } from "@primer/octicons-react";
import { ReactNode } from "react";
import { useAppSelector } from "../../hooks/useAppSelector";

/**
 * Banner tài chính trang đăng ký — số dư + tổng phải nộp + miễn giảm.
 * Poll mỗi 60s, refresh ngay sau register/cancel success (saga dispatches financial.LOAD_START).
 *
 * Render rule:
 *   - Cờ đợt off / data null → return null (đợt thường không liên quan tiền).
 *   - SV không có hồ sơ tài chính (!co_so_du) → số dư hiển thị 0đ (vd miễn giảm 100% không cần nộp,
 *     không có row ACC_SoDuSinhVien) — KHÔNG còn alert đỏ "chưa có hồ sơ".
 *   - vuotSoDu (conLai < 0): card cam cảnh báo + 4 stat tiles.
 *   - Bình thường: card xanh + 4 stat tiles.
 *
 * Layout responsive qua CSS Grid auto-fit:
 *   - Desktop ≥ 4 ô / hàng (Số dư · Đã ĐK · Còn lại · Miễn giảm).
 *   - Tablet 2 / hàng. Mobile 2 / hàng compact (font nhỏ hơn).
 */
const FinancialSummaryWidget = () => {
    const { data } = useAppSelector(s => s.financial);
    if (!data || !data.tinh_hoc_phi_theo_so_du) return null;

    const phanTramList = Array.from(new Set(data.mien_giam.map(m => m.phan_tram)));
    const congNoDotTruoc = data.da_dk_chua_nop ?? 0;
    const conLai = data.so_du - congNoDotTruoc - data.tong_tien_phai_nop;
    const vuotSoDu = conLai < 0;

    let updatedAtText = "";
    if (data.updated_at) {
        const d = new Date(data.updated_at);
        updatedAtText = d.toLocaleTimeString("vi-VN", { hour: "2-digit", minute: "2-digit" });
    }

    // ─── Card với header + 4 stat tiles ───
    const palette = vuotSoDu
        ? {
              bg: "#fff7ed",
              border: "#fed7aa",
              barBg: "#fff7ed",
              icon: "#9a3412",
              headerTxt: "#9a3412",
              accent: "#c2410c",
          }
        : {
              bg: "#ffffff",
              border: "#d6e9d6",
              barBg: "#f0fdf4",
              icon: "#166534",
              headerTxt: "#166534",
              accent: "#15803d",
          };

    const headerLabel = vuotSoDu
        ? "Tổng phải nộp vượt số dư — vui lòng nạp thêm"
        : "Tài chính sinh viên";

    return (
        <Box
            sx={{
                bg: palette.bg,
                border: `1px solid ${palette.border}`,
                borderRadius: 8,
                mb: 3,
                overflow: "hidden",
                boxShadow: "0 1px 2px rgba(0, 0, 0, 0.04)",
            }}
        >
            {/* Header bar */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    rowGap: 1,
                    columnGap: 3,
                    bg: palette.barBg,
                    borderBottom: `1px solid ${palette.border}`,
                    px: [2, 3],
                    py: "8px",
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0 }}>
                    <Box sx={{ color: palette.icon, display: "flex", flexShrink: 0 }}>
                        {vuotSoDu ? <AlertIcon size={16} /> : <CreditCardIcon size={16} />}
                    </Box>
                    <Text
                        sx={{
                            fontWeight: 700,
                            fontSize: [0, 1],
                            color: palette.headerTxt,
                            letterSpacing: "0.2px",
                        }}
                    >
                        {headerLabel}
                    </Text>
                </Box>
                {updatedAtText && (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            color: "#6b7280",
                            fontSize: 0,
                            fontStyle: "italic",
                        }}
                    >
                        <ClockIcon size={12} />
                        <Text>Cập nhật {updatedAtText}</Text>
                    </Box>
                )}
            </Box>

            {/* Stat tiles — grid auto-fit responsive */}
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: ["repeat(2, 1fr)", "repeat(2, 1fr)", "repeat(auto-fit, minmax(150px, 1fr))"],
                    bg: "#ffffff",
                }}
            >
                <StatTile
                    label="Số dư"
                    value={`${data.so_du.toLocaleString("vi-VN")}đ`}
                    valueColor="#111827"
                />
                <StatTile
                    label="Đã đăng ký"
                    value={`${data.tong_tien_phai_nop.toLocaleString("vi-VN")}đ`}
                    valueColor="#374151"
                    sub={data.tong_tien_phai_nop > 0
                        ? `${Math.min(100, Math.round((data.tong_tien_phai_nop / Math.max(1, data.so_du)) * 100))}% số dư`
                        : undefined}
                />
                {congNoDotTruoc > 0 && (
                    <StatTile
                        // label="ĐK chưa nộp đợt trước"
                        label="ĐK nộp đợt trước"
                        value={`${congNoDotTruoc.toLocaleString("vi-VN")}đ`}
                        valueColor="#b45309"
                        sub="đã trừ vào còn lại"
                    />
                )}
                <StatTile
                    label="Còn lại"
                    value={`${conLai.toLocaleString("vi-VN")}đ`}
                    valueColor={vuotSoDu ? "#b91c1c" : palette.accent}
                    strong
                />
                {phanTramList.length > 0 && (
                    <StatTile
                        label="Miễn giảm"
                        value={phanTramList.map(p => `${p}%`).join(", ")}
                        valueColor="#0e7490"
                    />
                )}
            </Box>
        </Box>
    );
};

/**
 * 1 ô thống kê — label nhỏ uppercase + giá trị lớn tabular-nums.
 * Divider mỏng giữa các ô (border-right desktop, border-bottom mobile khi wrap).
 */
const StatTile = ({
    label,
    value,
    valueColor,
    strong,
    sub,
}: {
    label: string;
    value: ReactNode;
    valueColor: string;
    /** Highlight ô (vd "Còn lại") với font lớn hơn. */
    strong?: boolean;
    /** Dòng phụ dưới value (vd "72% số dư"). */
    sub?: string;
}) => (
    <Box
        sx={{
            px: [2, 3],
            py: ["8px", "10px"],
            borderRight: "1px solid",
            borderBottom: "1px solid",
            borderColor: "#f0f1f3",
            // Bỏ border right ở ô cuối hàng + bottom ở hàng cuối — không control được chính xác
            // qua nth-child với auto-fit, nhưng border màu rất nhạt (#f0f1f3) nên divider thừa
            // không gây nhiễu thị giác. Chấp nhận trade-off code-simplicity.
        }}
    >
        <Text
            sx={{
                display: "block",
                fontSize: 0,
                color: "#6b7280",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.4px",
                mb: "2px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
            }}
        >
            {label}
        </Text>
        <Text
            sx={{
                display: "block",
                fontSize: strong ? [2, 3] : [1, 2],
                fontWeight: 700,
                color: valueColor,
                fontVariantNumeric: "tabular-nums",
                lineHeight: 1.25,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
            }}
        >
            {value}
        </Text>
        {sub && (
            <Text
                sx={{
                    display: "block",
                    fontSize: 0,
                    color: "#9ca3af",
                    mt: "2px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                }}
            >
                {sub}
            </Text>
        )}
    </Box>
);

export default FinancialSummaryWidget;
